import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import IdentityStep from './wizard/IdentityStep';
import InputsStep from './wizard/InputsStep';
import IngestStep from './wizard/IngestStep';
import PlanStep from './wizard/PlanStep';
import BuildStep from './wizard/BuildStep';
import SeoStep from './wizard/SeoStep';
import PublishStep from './wizard/PublishStep';
const STEPS = [
    { id: 1, title: 'Identity & tokens', blurb: 'Site name, URL, brand colors, fonts, libraries.', statusKey: 'identity' },
    { id: 2, title: 'Brand inputs', blurb: 'Upload PDFs, DOCX, MD or paste URLs that describe the brand.', statusKey: 'inputs' },
    { id: 3, title: 'Ingest', blurb: 'AI synthesizes a brand knowledge base from the inputs.', statusKey: 'ingest' },
    { id: 4, title: 'Plan', blurb: 'AI proposes the sitemap and per-page outlines.', statusKey: 'plan' },
    { id: 5, title: 'Build pages', blurb: 'Generate each page block-by-block with approval gates.', statusKey: 'build' },
    { id: 6, title: 'SEO pass', blurb: 'Meta length, JSON-LD validation, sitemap, robots, alt-text audit.', statusKey: 'seo' },
    { id: 7, title: 'Publish', blurb: 'Flip SITE_ENV to production and run a go-live checklist.', statusKey: 'publish' },
];
export default function WizardPage() {
    const [state, setState] = useState(null);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [params, setParams] = useSearchParams();
    const queryStepHandled = useRef(false);
    async function refresh() {
        setError(null);
        try {
            const { state, status } = await api.wizardState();
            setState(state);
            setStatus(status);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load wizard state');
        }
    }
    useEffect(() => { refresh(); }, []);
    async function setStep(step) {
        try {
            const { state } = await api.wizardSetStep(step);
            setState(state);
        }
        catch (e) {
            setError(e?.message ?? 'Failed');
        }
    }
    // Honor ?step=N (used by the dashboard's onboarding tiles). Only fires once
    // per mount to avoid fighting subsequent in-page navigations.
    useEffect(() => {
        if (queryStepHandled.current || !state)
            return;
        const q = parseInt(params.get('step') ?? '', 10);
        if (q >= 1 && q <= STEPS.length && q !== state.current_step) {
            queryStepHandled.current = true;
            setStep(q);
            // Clear the query param so the URL stops "remembering" the override.
            setParams({}, { replace: true });
        }
        else if (params.get('step')) {
            queryStepHandled.current = true;
            setParams({}, { replace: true });
        }
    }, [state, params, setParams]);
    if (!state || !status)
        return _jsx("div", { className: "page-shell", children: _jsx("div", { className: "muted", children: "Loading wizard\u2026" }) });
    const current = STEPS.find((s) => s.id === state.current_step) ?? STEPS[0];
    return (_jsxs("div", { className: "wizard", children: [_jsxs("aside", { className: "wizard-rail", children: [_jsxs("div", { className: "wizard-rail-head", children: [_jsx("h2", { children: "New site wizard" }), _jsx("p", { className: "muted small", children: "7-step guided setup. Resume any time." })] }), _jsx("ol", { className: "wizard-steps", children: STEPS.map((s) => {
                            const done = status[s.statusKey]?.done ?? false;
                            const active = state.current_step === s.id;
                            const reached = state.max_step_reached >= s.id;
                            return (_jsx("li", { className: `wizard-step${active ? ' active' : ''}${done ? ' done' : ''}${reached ? ' reached' : ''}`, children: _jsxs("button", { onClick: () => setStep(s.id), children: [_jsx("span", { className: "wizard-step-no", children: done ? '✓' : s.id }), _jsxs("span", { className: "wizard-step-meta", children: [_jsx("span", { className: "wizard-step-title", children: s.title }), _jsx("span", { className: "wizard-step-blurb", children: s.blurb })] })] }) }, s.id));
                        }) })] }), _jsxs("main", { className: "wizard-pane", children: [error && _jsx("div", { className: "err small pad-y", children: error }), _jsxs("div", { className: "wizard-pane-head", children: [_jsxs("span", { className: "muted small", children: ["Step ", current.id, " of ", STEPS.length] }), _jsx("h1", { children: current.title }), _jsx("p", { className: "muted", children: current.blurb })] }), current.id === 1 && _jsx(IdentityStep, { onAdvance: () => setStep(2), onChanged: refresh }), current.id === 2 && _jsx(InputsStep, { onAdvance: () => setStep(3), onChanged: refresh }), current.id === 3 && _jsx(IngestStep, { onAdvance: () => setStep(4), onChanged: refresh }), current.id === 4 && _jsx(PlanStep, { onAdvance: () => setStep(5), onChanged: refresh }), current.id === 5 && _jsx(BuildStep, { onAdvance: () => setStep(6), onChanged: refresh }), current.id === 6 && _jsx(SeoStep, { onAdvance: () => setStep(7), onChanged: refresh }), current.id === 7 && _jsx(PublishStep, { onChanged: refresh })] })] }));
}
